import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackbar } from "notistack";
import releaseNoteServices from "app/services/release-note-services";

const hoy = () => new Date().toISOString().slice(0, 10);

// Donde se ve la tanda (ReleaseNote::AUDIENCES).
export const AUDIENCIAS = [
    { value: "both", label: "Web y app" },
    { value: "web", label: "Solo web" },
    { value: "app", label: "Solo app" },
];

const MB = 1024 * 1024;

// Un archivo adjunto de la tanda: el PDF o el video. Lo que ya estaba guardado se muestra por
// su nombre; quitarlo solo se manda al guardar.
const useAdjunto = ({ tipo, maxBytes, errorTipo, errorPeso, nombreGuardado }) => {
    const [archivo, setArchivo] = useState(null);
    const [quitar, setQuitar] = useState(false);
    const [error, setError] = useState("");

    const reiniciar = () => { setArchivo(null); setQuitar(false); setError(""); };

    const elegir = (event) => {
        const elegido = event.target.files?.[0];
        event.target.value = "";
        if (!elegido) return;
        if (elegido.type !== tipo) return setError(errorTipo);
        if (elegido.size > maxBytes) return setError(errorPeso);
        setError("");
        setArchivo(elegido);
        setQuitar(false);
    };

    const sacar = () => { setArchivo(null); setQuitar(Boolean(nombreGuardado)); };

    const actual = archivo?.name || (!quitar && nombreGuardado) || null;
    return { archivo, quitar, error, actual, elegir, sacar, reiniciar };
};

const AdjuntoRow = ({ adjunto, accept, adjuntar, cambiar, ayuda }) => (
    <Stack spacing={0.75}>
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Button variant="outlined" component="label" size="small" sx={{ flexShrink: 0 }}>
                {adjunto.actual ? cambiar : adjuntar}
                <input hidden type="file" accept={accept} onChange={adjunto.elegir} />
            </Button>
            {adjunto.actual ? (
                <>
                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{adjunto.actual}</Typography>
                    <Button size="small" color="error" onClick={adjunto.sacar}>Quitar</Button>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">{ayuda}</Typography>
            )}
        </Stack>
        {adjunto.error && <Alert severity="error">{adjunto.error}</Alert>}
    </Stack>
);

const ReleaseNoteFormDialog = ({ open = false, record = null, onClose = () => {}, onSaved = () => {} }) => {
    const { enqueueSnackbar } = useSnackbar();
    const editando = Boolean(record);

    const [releasedOn, setReleasedOn] = useState(hoy());
    const [minAppVersion, setMinAppVersion] = useState("");
    const [texto, setTexto] = useState("");
    const [publicar, setPublicar] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");
    const [audiencia, setAudiencia] = useState("both");
    const documento = useAdjunto({
        tipo: "application/pdf",
        maxBytes: 20 * MB,
        errorTipo: "Tiene que ser un PDF.",
        errorPeso: "El PDF no puede pesar más de 20 MB.",
        nombreGuardado: record?.document_name,
    });
    const video = useAdjunto({
        tipo: "video/mp4",
        maxBytes: 80 * MB,
        errorTipo: "Tiene que ser un video MP4.",
        errorPeso: "El video no puede pesar más de 80 MB.",
        nombreGuardado: record?.video_file_name,
    });
    const [saving, setSaving] = useState(false);

    // Se reinicia en cada apertura: sin esto, lo escrito para una tanda aparece en la siguiente.
    useEffect(() => {
        if (!open) return;
        setReleasedOn(record?.released_on || hoy());
        setTexto((record?.items || []).join("\n"));
        setMinAppVersion(record?.min_app_version || "");
        setPublicar(false);
        setVideoUrl(record?.video_url || "");
        setAudiencia(record?.audience || "both");
        documento.reiniciar();
        video.reiniciar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record]);


    const items = useMemo(
        () => texto.split("\n").map((linea) => linea.trim()).filter(Boolean),
        [texto],
    );

    const puedeGuardar = Boolean(releasedOn) && items.length > 0;

    const guardar = async () => {
        if (!puedeGuardar) return;

        try {
            setSaving(true);
            const payload = {
                released_on: releasedOn,
                items,
                min_app_version: minAppVersion.trim(),
                video_url: videoUrl.trim(),
                audience: audiencia,
            };
            if (!editando && publicar) payload.publish = "1";
            if (documento.archivo) payload.document = documento.archivo;
            else if (documento.quitar) payload.remove_document = "1";
            if (video.archivo) payload.video_file = video.archivo;
            else if (video.quitar) payload.remove_video_file = "1";

            const guardada = editando
                ? await releaseNoteServices.updateReleaseNote(record.id, payload)
                : await releaseNoteServices.createReleaseNote(payload);

            enqueueSnackbar(
                editando ? "Novedades actualizadas" : (publicar ? "Novedades publicadas" : "Borrador guardado"),
                { variant: "success" },
            );
            onSaved(guardada);
            onClose();
        } catch (_error) {
            // El interceptor global ya muestra el mensaje.
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
            <DialogTitle>
                <Typography variant="h2" color="primary">
                    {editando ? `Novedades del ${record?.released_on}` : "Nuevas novedades"}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Fecha de la tanda"
                        InputLabelProps={{ shrink: true }}
                        value={releasedOn}
                        onChange={(event) => setReleasedOn(event.target.value)}
                        helperText="Es la fecha del release, no la de hoy. La app solo le muestra estas novedades a quien ya tenga una versión de esa fecha o posterior."
                    />

                    <TextField
                        fullWidth
                        multiline
                        minRows={7}
                        label="Cambios, uno por línea"
                        placeholder={"Nuevo menú lateral: se abre deslizando desde el borde izquierdo.\nDesde ahí puedes cerrar sesión, algo que antes no se podía."}
                        value={texto}
                        onChange={(event) => setTexto(event.target.value)}
                        helperText={`${items.length} ${items.length === 1 ? "cambio" : "cambios"}. Escribe lo que la persona ve o hace distinto, sin detalle técnico.`}
                    />

                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Dónde se ve"
                        value={audiencia}
                        onChange={(event) => setAudiencia(event.target.value)}
                        helperText="Una novedad solo de la web no aparece en la app, y al revés."
                    >
                        {AUDIENCIAS.map((a) => (
                            <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        size="small"
                        label="Versión mínima de la app (opcional)"
                        placeholder="2.1"
                        value={minAppVersion}
                        onChange={(event) => setMinAppVersion(event.target.value)}
                        helperText="Complétalo solo si el cambio necesita una versión nueva de la app, por ejemplo cuando se agrega algo que no llega por actualización automática. Quien tenga una versión anterior no verá esta tanda."
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Video que lo explica (opcional)"
                        placeholder="https://youtu.be/..."
                        value={videoUrl}
                        onChange={(event) => setVideoUrl(event.target.value)}
                        helperText="Link de YouTube (no listado), Vimeo, Loom o Drive. Quien tenga el link puede verlo: grábalo con la empresa Haulers Demo, sin datos de clientes."
                    />

                    <AdjuntoRow
                        adjunto={video}
                        accept="video/mp4"
                        adjuntar="Subir video MP4"
                        cambiar="Cambiar video"
                        ayuda="O súbelo aquí, hasta 80 MB: queda privado y se ve dentro de la web y de la app."
                    />

                    <AdjuntoRow
                        adjunto={documento}
                        accept="application/pdf"
                        adjuntar="Adjuntar PDF"
                        cambiar="Cambiar PDF"
                        ayuda="Opcional, hasta 20 MB."
                    />

                    {!editando && (
                        <FormControlLabel
                            control={<Checkbox checked={publicar} onChange={(event) => setPublicar(event.target.checked)} />}
                            label="Publicar ahora"
                        />
                    )}

                    {!editando && !publicar && (
                        <Alert severity="info">
                            Se guarda como borrador. Nadie la ve hasta que la publiques.
                        </Alert>
                    )}

                    {editando && !record?.draft && (
                        <Alert severity="warning">
                            Esta tanda ya está publicada: los cambios se ven de inmediato en la app.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton variant="contained" loading={saving} disabled={!puedeGuardar} onClick={guardar}>
                    {editando ? "Guardar" : (publicar ? "Publicar" : "Guardar borrador")}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ReleaseNoteFormDialog;
